import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.franchisee.view.FranchiseeGuidesView.';

  public static readonly guides = `${Id.prefix}guides`;
  public static readonly exportFormat = `${Id.prefix}exportFormat`;

  public static readonly plainWhatItIs = `${Id.prefix}plainWhatItIs`;
  public static readonly plainCostWarningSummary = `${Id.prefix}plainCostWarningSummary`;
  public static readonly plainCostWarning = `${Id.prefix}plainCostWarning`;

  public static readonly technicalHeading = `${Id.prefix}technicalHeading`;
  public static readonly technicalIntro = `${Id.prefix}technicalIntro`;
  public static readonly conventionsSummary = `${Id.prefix}conventionsSummary`;
  public static readonly conventions = `${Id.prefix}conventions`;
  public static readonly structureSummary = `${Id.prefix}structureSummary`;
  public static readonly structure = `${Id.prefix}structure`;
  public static readonly stabilitySummary = `${Id.prefix}stabilitySummary`;
  public static readonly stability = `${Id.prefix}stability`;

  public static readonly quoteFieldsSummary = `${Id.prefix}quoteFieldsSummary`;
  public static readonly orderFieldsSummary = `${Id.prefix}orderFieldsSummary`;
  public static readonly fieldsIntro = `${Id.prefix}fieldsIntro`;
  public static readonly columnPath = `${Id.prefix}columnPath`;
  public static readonly columnType = `${Id.prefix}columnType`;
  public static readonly columnPresence = `${Id.prefix}columnPresence`;
  public static readonly columnMeaning = `${Id.prefix}columnMeaning`;

  public static readonly samplesSummary = `${Id.prefix}samplesSummary`;
  public static readonly samplesIntro = `${Id.prefix}samplesIntro`;
  public static readonly sampleQuoteJson = `${Id.prefix}sampleQuoteJson`;
  public static readonly sampleQuoteXml = `${Id.prefix}sampleQuoteXml`;
  public static readonly sampleOrderJson = `${Id.prefix}sampleOrderJson`;
  public static readonly sampleOrderXml = `${Id.prefix}sampleOrderXml`;

  public static readonly schemaVersion = `${Id.prefix}schemaVersion`;
}

export class LangFranchiseeGuidesView {
  private constructor() {}

  public static guides(): string {
    return interpolate(Id.guides);
  }
  public static exportFormat(): string {
    return interpolate(Id.exportFormat);
  }
  public static plainWhatItIs(): string {
    return interpolate(Id.plainWhatItIs);
  }
  public static plainCostWarningSummary(): string {
    return interpolate(Id.plainCostWarningSummary);
  }
  public static plainCostWarning(): string {
    return interpolate(Id.plainCostWarning);
  }
  public static technicalHeading(): string {
    return interpolate(Id.technicalHeading);
  }
  public static technicalIntro(): string {
    return interpolate(Id.technicalIntro);
  }
  public static conventionsSummary(): string {
    return interpolate(Id.conventionsSummary);
  }
  public static conventions(): string {
    return interpolate(Id.conventions);
  }
  public static structureSummary(): string {
    return interpolate(Id.structureSummary);
  }
  public static structure(): string {
    return interpolate(Id.structure);
  }
  public static stabilitySummary(): string {
    return interpolate(Id.stabilitySummary);
  }
  public static stability(): string {
    return interpolate(Id.stability);
  }
  public static quoteFieldsSummary(): string {
    return interpolate(Id.quoteFieldsSummary);
  }
  public static orderFieldsSummary(): string {
    return interpolate(Id.orderFieldsSummary);
  }
  public static fieldsIntro(): string {
    return interpolate(Id.fieldsIntro);
  }
  public static columnPath(): string {
    return interpolate(Id.columnPath);
  }
  public static columnType(): string {
    return interpolate(Id.columnType);
  }
  public static columnPresence(): string {
    return interpolate(Id.columnPresence);
  }
  public static columnMeaning(): string {
    return interpolate(Id.columnMeaning);
  }
  public static samplesSummary(): string {
    return interpolate(Id.samplesSummary);
  }
  public static samplesIntro(): string {
    return interpolate(Id.samplesIntro);
  }
  public static sampleQuoteJson(): string {
    return interpolate(Id.sampleQuoteJson);
  }
  public static sampleQuoteXml(): string {
    return interpolate(Id.sampleQuoteXml);
  }
  public static sampleOrderJson(): string {
    return interpolate(Id.sampleOrderJson);
  }
  public static sampleOrderXml(): string {
    return interpolate(Id.sampleOrderXml);
  }
  public static schemaVersion(params: { version: string }): string {
    return interpolate(Id.schemaVersion, params);
  }
}

/**
 * The Export Format guide's text.
 *
 * NOTE ON LINE BREAKS: `asMarkdownTemplate` runs marked with `breaks: true`, so every newline
 * inside one of these strings becomes a forced `<br>` in the rendered page. A paragraph must
 * therefore be a single line here, however long it reads in source. Wrapping one for the sake of
 * the editor puts a line break into the middle of a customer's sentence.
 */
export class FranchiseeGuidesViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.guides, 'Guides', 'Caption for the Guides settings section');
    register.add(Id.exportFormat, 'Export Format', 'Title of the export format guide');

    // The plain language half. Opens with what the file is and what it is for, in words someone
    // who does not write software would use, and does not mention a format, a field or a schema
    // until the technical half below.
    register.add(
      Id.plainWhatItIs,
      `Every time you issue a {{glossary 'quote'}} or a {{glossary 'purchase-order'}}, we save a data file of it alongside the usual PDFs. It holds the same information you can see on screen, written in a way another computer system can read.

You would use it to get a {{glossary 'quote'}} into your accounting, ordering or reporting system without anyone re-typing it. Most businesses set this up once: you show your other system the file, tell it which of your own fields each piece of information belongs in, and from then on it knows what to do with every file you send it.

**When it is created.** At the moment you issue the document, and never again. It is a record of exactly what you issued, so nothing that happens afterwards changes it.

**Where to find it.** In the Documents tab of the project, and in the reports menu on the {{glossary 'quote'}} or {{glossary 'purchase-order'}} itself, next to the PDFs. There are two files each time, one ending **.json** and one ending **.xml**. They contain exactly the same information; use whichever your other system prefers.

**Documents issued before this feature was released do not have one.** We do not create them after the fact, because a file built today would describe the {{glossary 'quote'}} as it is now rather than as it was when you issued it, which would defeat the point of it.`,
      'Plain language explanation of the export file, shown first in the guide'
    );

    register.add(Id.plainCostWarningSummary, 'Before you share this file', 'Heading for the cost data warning');
    register.add(
      Id.plainCostWarning,
      `**This file contains your cost and margin figures.** It is built for your own systems, so it includes what each item cost you, the margin you applied, your buying multiplier and any supplier adjustment, alongside the prices your client sees.

That is deliberate and it is your data. But it does mean this is not a file to forward to a client or to a supplier. If you need to send someone the {{glossary 'quote'}}, send them the PDF.`,
      'Warning that the export contains dealer cost and margin data'
    );

    register.add(Id.technicalHeading, 'Technical specification', 'Heading for the technical half of the guide');
    register.add(
      Id.technicalIntro,
      `This half is written for whoever connects the file to your other system. It assumes you have a real export open in front of you.

Both formats carry the same fields under the same names, with the same spelling and capitalisation, so a mapping built against one translates directly to the other. The field names are part of the format and are the same in every language; only this guide is translated.`,
      'Introduction to the technical section'
    );

    register.add(Id.conventionsSummary, 'How values are written', 'Heading for file-wide conventions');
    register.add(
      Id.conventions,
      `These hold everywhere in the file.

- **Text encoding** is UTF-8, with no byte order mark.
- **Dates** are written as \`2026-09-23\` (year, month, day).
- **Times** are written as \`2026-09-23T14:05:09+02:00\`, with the offset from UTC on the end.
- **Numbers** use a full stop for the decimal point, never a thousands separator, and a leading minus sign for negatives, for example \`-25.50\`. The number of decimal places is not fixed, so read them as decimal numbers rather than matching a pattern.
- **Money** is written as a plain number with no currency symbol. The currency is stated once, in the \`currency\` field at the top of the file, and applies to every monetary value in it.
- **Percentages** are written as the percentage, so a 35 percent margin is \`35\`, not \`0.35\`.
- **Identifiers** are our internal references. They are stable for the life of the record and are useful as a key to match against, but they are not meant to be shown to anyone.`,
      'File-wide conventions for dates, numbers, money and encoding'
    );

    register.add(Id.structureSummary, 'Repeating items and missing values', 'Heading for structure notes');
    register.add(
      Id.structure,
      `**Lists.** Line items sit in a \`lineItems\` list, and each entry is a \`lineItem\`. Delivery batches on a {{glossary 'purchase-order'}} work the same way. A document with one line item still has the list, with one entry in it, and a document with none still has the list, empty. Your mapping should not treat one item as a special case.

**Missing values.** A field we have no value for is left out of the file entirely. It is never written as an empty string, a zero, or a placeholder. That means **a zero always means zero**: if \`tax\` is \`0\` the line genuinely has no tax to pay, and the separate \`taxable\` field tells you whether that is because the line is exempt.

**Some fields never apply to some lines.** A buy-in line has no supplier list price, buying multiplier or dealer adjustment, because you sourced the stock yourself, so those fields are absent rather than zero. A line whose price is made up of a breakdown carries no cost figures of its own.

**Nesting.** Related fields are grouped, for example the client sits under \`quote/client\`. The field reference below gives the full position of every field.`,
      'How lists, missing values and nesting work in the file'
    );

    register.add(Id.stabilitySummary, 'What will and will not change', 'Heading for the stability and versioning note');
    register.add(
      Id.stability,
      `Every file carries a \`schemaVersion\`. Read it, and check it, because it is how you find out something has changed.

Within a version we will **not** rename a field, move it, change its type, or change how a value is written. Those are the things that would break a mapping you have already built, so they only happen with a new version.

We may **add** new fields within a version. A mapping that ignores fields it does not recognise will keep working; one that rejects anything unexpected will not, so set yours up to ignore what it does not need.

We will tell you before a version changes, through the usual release notes, and both versions will be readable for long enough to move across.`,
      'Schema version and compatibility promises'
    );

    register.add(Id.quoteFieldsSummary, 'Quote export fields', 'Heading for the quote field reference table');
    register.add(
      Id.orderFieldsSummary,
      'Purchase order export fields',
      'Heading for the purchase order field reference table'
    );
    register.add(
      Id.fieldsIntro,
      `Every field in the file, in the order it appears. **Path** is where the field sits, with a slash for each level of nesting. **Presence** says whether the field is in every file, or left out when there is no value for it.`,
      'Introduction to the field reference tables'
    );
    register.add(Id.columnPath, 'Path', 'Field reference table column heading');
    register.add(Id.columnType, 'Type', 'Field reference table column heading');
    register.add(Id.columnPresence, 'Presence', 'Field reference table column heading');
    register.add(Id.columnMeaning, 'Meaning', 'Field reference table column heading');

    register.add(Id.samplesSummary, 'Worked examples', 'Heading for the sample files');
    register.add(
      Id.samplesIntro,
      `Complete examples of both documents in both formats. They are generated from the same definition the real files are written from, so their structure and field names match exactly what you will receive. The values are made up.`,
      'Introduction to the worked sample files'
    );
    register.add(Id.sampleQuoteJson, 'Quote, JSON', 'Heading for the quote JSON sample');
    register.add(Id.sampleQuoteXml, 'Quote, XML', 'Heading for the quote XML sample');
    register.add(Id.sampleOrderJson, 'Purchase order, JSON', 'Heading for the purchase order JSON sample');
    register.add(Id.sampleOrderXml, 'Purchase order, XML', 'Heading for the purchase order XML sample');

    register.add(
      Id.schemaVersion,
      'This guide describes schema version **{{version}}**.',
      'States which schema version the guide documents'
    );
  }
}
